import type { Me } from './api.js';

/**
 * WHO THIS SESSION AUTHENTICATED AS, for a screen that needs to say so.
 *
 * A one-line function with a docblock this size because the line it replaces
 * was `me.email`, and that is the whole defect the founder met on staging:
 *
 *     Signed in as owner@phibian-demo.example
 *     Confirm your password for owner@phibian-demo.example
 *
 * He had signed in with his real Google account. `owner@phibian-demo.example`
 * is `app_user.email` — the application PROFILE on the persona he holds inside
 * the demo organization — and the identity behind the session is federated-only,
 * so the password the second sentence asked for does not exist.
 *
 * THE ABSENT CASE IS THE POINT OF THE FUNCTION. When the server states no
 * authentication identity — a bundle talking to an older API, a deployment with
 * no identity provider, a session predating the observation — this returns
 * `undefined` and the caller says NOTHING about who is signed in. It does not
 * fall back to {@link Me.email}. There is a standing temptation to, because an
 * unattributed "Signed in" line looks unfinished; falling back is how the
 * original sentence got written, and a screen that names the wrong person is
 * worse than one that names nobody.
 *
 * Deliberately a named function rather than an inline `?.` at the call site, so
 * that the rule has somewhere to be tested and somewhere to be read. See
 * `apps/admin/test/unit/authn-identity-display.test.ts`.
 */
export function signedInAs(
  me: Pick<Me, 'authnIdentity'> & { readonly email?: string },
): string | undefined {
  const authn = me.authnIdentity;
  if (!authn || authn.observed !== true) return undefined;
  const email = authn.email;
  if (typeof email !== 'string' || email.trim() === '') return undefined;
  return email;
}
